import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2, X, TrendingUp, Users, Calculator, ShoppingCart, Truck, Factory,
  BarChart3, FileText, ChevronRight, Lock, Receipt, Share2, Building2,
} from 'lucide-react';
import { reportService } from '../../services/reportService';

// =============================================================================
// Reporting (MOD_10_REPORT) — the landing page.
//
// One module, one section per operational part of the business: Operating
// Expenses, Procurement, Manufacturing, Distribution, Planning, Capital,
// Revenue, People, Finance.
//
// ACCESS FOLLOWS THE MODULE. A section names the module it reports on, so
// holding Distribution is what opens the Distribution reports — nobody
// maintains a second list of who may read what. role_report_access survives as
// an additive OVERRIDE for the genuine exception (the category manager who
// needs the Finance pack without the Finance module).
//
// THE ONE CARVE-OUT IS PEOPLE, and it is the point of the design rather than a
// footnote: a section whose module is flagged is_sensitive needs that module
// explicitly, and is never covered by the administrator blanket. Otherwise
// reporting is the back door into salaries that the sidebar carefully closes.
//
// A section the acting role cannot open is ABSENT, not greyed out. A locked
// door you can see the shape of is just an invitation to ask why it is locked.
// The count at the bottom says how many exist, which is enough for someone to
// know to go and ask.
//
// THE CATALOGUE IS DATA. Sections come from report_sections and reports from
// report_definitions, so adding either is a row, not a release. This page
// renders whatever the server says exists.
// =============================================================================

// Section icons are stored as lucide names on the section row, resolved here
// against an allow-list — a new section picks its own icon without a front-end
// change, and an unknown name degrades to a generic one rather than crashing.
const ICONS = {
  TrendingUp, Users, Calculator, ShoppingCart, Truck, Factory, BarChart3, FileText,
  Receipt, Share2, Building2,
};
const iconFor = (name) => ICONS[name] || BarChart3;

export default function Reports() {
  const navigate = useNavigate();
  const [data, setData] = useState({ sections: [], total_sections: 0, sees_all: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await reportService.getSections();
      setData(res.data || { sections: [], total_sections: 0 });
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not load the report catalogue.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const hidden = (data.total_sections || 0) - (data.sections || []).length;

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900 overflow-y-auto">
      <div className="px-6 pt-6 pb-4 shrink-0">
        <h1 className="m-0 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Reporting</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Reports grouped by the part of the business that reads them. Pick a section to see what is in it.
        </p>

        {error && (
          <div className="mt-3 flex items-start justify-between gap-2 px-3 py-2 rounded-lg border border-red-300 bg-red-50 dark:border-red-500/40 dark:bg-red-500/10">
            <p className="m-0 text-xs text-red-700 dark:text-red-300">{error}</p>
            <button onClick={() => setError('')} className="text-red-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
          </div>
        )}
      </div>

      <div className="flex-1 px-6 pb-6">
        {loading ? (
          <div className="h-40 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : data.sections.length === 0 ? (
          <div className="max-w-xl mx-auto mt-10 px-4 py-6 rounded-xl border border-dashed border-slate-300 dark:border-slate-600 text-center">
            <Lock className="w-5 h-5 mx-auto text-slate-400" />
            <p className="mt-2 mb-0 text-sm font-semibold text-slate-700 dark:text-slate-200">
              No reporting sections have been granted to this role.
            </p>
            <p className="mt-1.5 mb-0 text-xs text-slate-500 dark:text-slate-400">
              {data.total_sections > 0
                ? `${data.total_sections} section${data.total_sections === 1 ? ' exists' : 's exist'}. A reporting section opens when this role holds the module it reports on — an administrator grants modules in Settings › Role Data Access.`
                : 'Nothing is in the catalogue yet.'}
            </p>
          </div>
        ) : (
          <>
            <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(300px,1fr))]">
              {data.sections.map((s) => {
                const Icon = iconFor(s.icon);
                return (
                  <button
                    key={s.section_code}
                    onClick={() => navigate(`/reports/${s.section_code}`)}
                    className="group text-left p-5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-slate-400 dark:hover:border-slate-500 hover:shadow-md transition-all"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200">
                        <Icon className="w-5 h-5" />
                      </span>
                      <ChevronRight className="w-4 h-4 mt-3 text-slate-300 group-hover:text-slate-500 dark:text-slate-600 dark:group-hover:text-slate-400 transition-colors" />
                    </div>
                    <h2 className="mt-3 mb-0 text-base font-bold text-slate-900 dark:text-slate-100">{s.section_name}</h2>
                    <p className="mt-1 mb-0 text-xs leading-relaxed text-slate-500 dark:text-slate-400">{s.description}</p>
                    {/* Which module opened this tile. Named because "why can I
                        see this and my colleague cannot" is the first question
                        anyone asks, and the answer is now a module grant. */}
                    <span className="mt-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                      <span>{s.report_count} report{s.report_count === 1 ? '' : 's'}</span>
                      {s.module_name && (
                        <>
                          <span className="text-slate-300 dark:text-slate-600">·</span>
                          <span className="normal-case font-medium tracking-normal">{s.module_name}</span>
                        </>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Said plainly rather than left as a silence. Someone who knows a
                section exists can go and ask for it; someone who does not think
                it exists never will. */}
            {hidden > 0 && !data.sees_all && (
              <p className="mt-4 mb-0 text-[11px] text-slate-400">
                {hidden} further section{hidden === 1 ? '' : 's'} exist{hidden === 1 ? 's' : ''} that this role cannot
                open. A section follows its module, so the grant to ask for is the module itself &mdash; in Settings
                &rsaquo; Role Data Access.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
